'use client';

import { c as _c } from "react/compiler-runtime";
import './gift-giving.scss';
import type { PropsWithChildren } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Divider } from '@sola/ark-core/atoms/divider/divider.tsx';
import { IntlBlock } from '@sola/ark-core/atoms/intl/Intl.tsx';
import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.tsx';
import { Sizes, Text, Variants } from '@sola/ark-core/atoms/text/text.tsx';
import { ActionCard } from '@sola/ark-core/molecules/action-card/action-card.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import useCollapsible from '@sola/ark-core/utils/hooks/useCollapsible.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { useContentSettings } from '@sola/sdg-sdk/hooks.ts';
const getTranslationKeyGiftType = (giftType: string) => `my:gifts.${giftType}.section`;
interface GiftGivingAlertProps {
  text?: React.ReactNode;
  isLoading?: boolean;
}
const GiftGivingAlert: React.FC<GiftGivingAlertProps> = t0 => {
  const $ = _c(3);
  const {
    text,
    isLoading: t1
  } = t0;
  const isLoading = t1 === undefined ? true : t1;
  if (!text) {
    return;
  }
  if (isLoading) {
    let t2;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <Skeleton height="4rem" />;
      $[0] = t2;
    } else {
      t2 = $[0];
    }
    return t2;
  }
  let t2;
  if ($[1] !== text) {
    t2 = <Alert variant="neutral" message={text} />;
    $[1] = text;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
interface GiftGivingDescriptionProps {
  text: string;
}
const GiftGivingDescription: React.FC<GiftGivingDescriptionProps> = t0 => {
  const $ = _c(2);
  const {
    text
  } = t0;
  let t1;
  if ($[0] !== text) {
    t1 = <Text size={Sizes.Small} asChild={true}><p className="body-neutral-mild mt-snug-lg">{text}</p></Text>;
    $[0] = text;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
interface GiftGivingSectionProps extends PropsWithChildren {
  title?: string;
}
const GiftGivingSection: React.FC<GiftGivingSectionProps> = t0 => {
  const $ = _c(12);
  const {
    title,
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      defaultExpanded: true
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    triggerProps,
    targetProps
  } = useCollapsible(t1);
  let t2;
  if ($[1] !== children || $[2] !== targetProps || $[3] !== title || $[4] !== triggerProps) {
    const {
      className: targetClasses,
      hide: targetHidden,
      ...contentTargetProps
    } = targetProps;
    let t3;
    if ($[6] !== title) {
      t3 = title && <Text variant={Variants.Heading} size={Sizes.Small} asChild={true}><h3 className="GivingInfoSection__title">{title}</h3></Text>;
      $[6] = title;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    let t4;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <ArkIcon iconName="chevron-up" className="GivingInfoSection__chevron shrink-0" size="1.5rem" />;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    let t5;
    if ($[9] !== t3 || $[10] !== triggerProps) {
      t5 = <div className="GivingInfoSection__header flex items-center justify-between gap-comfy-xs md:gap-comfy-sm w-full" {...triggerProps}>{t3}{t4}</div>;
      $[9] = t3;
      $[10] = triggerProps;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    t2 = <section>{t5}{!targetHidden && <div className={cn("flex flex-col gap-comfy-xl", targetClasses, {
        ...contentTargetProps
      })}>{children}</div>}</section>;
    $[1] = children;
    $[2] = targetProps;
    $[3] = title;
    $[4] = triggerProps;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};

/**
 * Displays a list of options for giving gifts to commitments.
 */
export const GiftGiving: React.FC = () => {
  const $ = _c(97);
  const {
    t
  } = useTranslation("my");
  const {
    data: contentSettings,
    isLoading: isContentSettingsLoading
  } = useContentSettings();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getTranslationKeyGiftType("birthday");
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const birthdayTranslationKey = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getTranslationKeyGiftType("child");
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const childTranslationKey = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = getTranslationKeyGiftType("family");
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const familyTranslationKey = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = getTranslationKeyGiftType("center");
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const centerTranslationKey = t3;
  let t4;
  if ($[4] !== t) {
    t4 = t(`${birthdayTranslationKey}.title`);
    $[4] = t;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t) {
    t5 = t(`${birthdayTranslationKey}.message`);
    $[6] = t;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t5) {
    t6 = <GiftGivingDescription text={t5} />;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = contentSettings?.giftAmountsBirthdayMin ?? 10;
  let t8;
  if ($[10] !== t || $[11] !== t7) {
    t8 = t("core:numbers.currencyCustom", {
      amount: t7
    });
    $[10] = t;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const t9 = contentSettings?.giftAmountsBirthdayMax ?? 150;
  let t10;
  if ($[13] !== t || $[14] !== t9) {
    t10 = t("core:numbers.currencyCustom", {
      amount: t9
    });
    $[13] = t;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10 || $[17] !== t8) {
    t11 = <IntlBlock i18nKey={`${birthdayTranslationKey}.alertText`} values={{
      min: t8,
      max: t10
    }} />;
    $[16] = t10;
    $[17] = t8;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== isContentSettingsLoading || $[20] !== t11) {
    t12 = <GiftGivingAlert text={t11} isLoading={isContentSettingsLoading} />;
    $[19] = isContentSettingsLoading;
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="flex flex-col gap-comfy-xs"><ActionCard title="Birthday Test Card Title 1" subtitle="This is a line of dummy text that represents the subtitle" href="#" /><ActionCard title="Birthday Test Card Title 2" subtitle="This is a line of dummy text that represents the subtitle" href="#" /></div>;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== t12 || $[24] !== t4 || $[25] !== t6) {
    t14 = <GiftGivingSection title={t4}>{t6}{t12}{t13}</GiftGivingSection>;
    $[23] = t12;
    $[24] = t4;
    $[25] = t6;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Divider className="my-comfy-xl" purpose="neutral" />;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== t) {
    t16 = t(`${childTranslationKey}.title`);
    $[28] = t;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== t) {
    t17 = t(`${childTranslationKey}.message`);
    $[30] = t;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== t17) {
    t18 = <GiftGivingDescription text={t17} />;
    $[32] = t17;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  const t19 = contentSettings?.giftAmountsChildMin ?? 10;
  let t20;
  if ($[34] !== t || $[35] !== t19) {
    t20 = t("core:numbers.currencyCustom", {
      amount: t19
    });
    $[34] = t;
    $[35] = t19;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  const t21 = contentSettings?.giftAmountsChildMax ?? 150;
  let t22;
  if ($[37] !== t || $[38] !== t21) {
    t22 = t("core:numbers.currencyCustom", {
      amount: t21
    });
    $[37] = t;
    $[38] = t21;
    $[39] = t22;
  } else {
    t22 = $[39];
  }
  let t23;
  if ($[40] !== t20 || $[41] !== t22) {
    t23 = <IntlBlock i18nKey={`${childTranslationKey}.alertText`} values={{
      min: t20,
      max: t22
    }} />;
    $[40] = t20;
    $[41] = t22;
    $[42] = t23;
  } else {
    t23 = $[42];
  }
  let t24;
  if ($[43] !== isContentSettingsLoading || $[44] !== t23) {
    t24 = <GiftGivingAlert text={t23} isLoading={isContentSettingsLoading} />;
    $[43] = isContentSettingsLoading;
    $[44] = t23;
    $[45] = t24;
  } else {
    t24 = $[45];
  }
  let t25;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <div className="flex flex-col gap-comfy-xs"><ActionCard title="Child Test Card Title 1" subtitle="This is a line of dummy text that represents the subtitle" href="#" /><ActionCard title="Child Test Card Title 2" subtitle="This is a line of dummy text that represents the subtitle" href="#" /></div>;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  let t26;
  if ($[47] !== t16 || $[48] !== t18 || $[49] !== t24) {
    t26 = <GiftGivingSection title={t16}>{t18}{t24}{t25}</GiftGivingSection>;
    $[47] = t16;
    $[48] = t18;
    $[49] = t24;
    $[50] = t26;
  } else {
    t26 = $[50];
  }
  let t27;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <Divider className="my-comfy-xl" purpose="neutral" />;
    $[51] = t27;
  } else {
    t27 = $[51];
  }
  let t28;
  if ($[52] !== t) {
    t28 = t(`${familyTranslationKey}.title`);
    $[52] = t;
    $[53] = t28;
  } else {
    t28 = $[53];
  }
  let t29;
  if ($[54] !== t) {
    t29 = t(`${familyTranslationKey}.message`);
    $[54] = t;
    $[55] = t29;
  } else {
    t29 = $[55];
  }
  let t30;
  if ($[56] !== t29) {
    t30 = <GiftGivingDescription text={t29} />;
    $[56] = t29;
    $[57] = t30;
  } else {
    t30 = $[57];
  }
  const t31 = contentSettings?.giftAmountsFamilyMax ?? 1100;
  let t32;
  if ($[58] !== t || $[59] !== t31) {
    t32 = t("core:numbers.currencyCustom", {
      amount: t31
    });
    $[58] = t;
    $[59] = t31;
    $[60] = t32;
  } else {
    t32 = $[60];
  }
  let t33;
  if ($[61] !== t32) {
    t33 = <IntlBlock i18nKey={`${familyTranslationKey}.alertText`} values={{
      max: t32
    }} />;
    $[61] = t32;
    $[62] = t33;
  } else {
    t33 = $[62];
  }
  let t34;
  if ($[63] !== isContentSettingsLoading || $[64] !== t33) {
    t34 = <GiftGivingAlert text={t33} isLoading={isContentSettingsLoading} />;
    $[63] = isContentSettingsLoading;
    $[64] = t33;
    $[65] = t34;
  } else {
    t34 = $[65];
  }
  let t35;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <div className="flex flex-col gap-comfy-xs"><ActionCard title="Family Test Card Title 1" subtitle="This is a line of dummy text that represents the subtitle" href="#" /><ActionCard title="Family Test Card Title 2" subtitle="This is a line of dummy text that represents the subtitle" href="#" /></div>;
    $[66] = t35;
  } else {
    t35 = $[66];
  }
  let t36;
  if ($[67] !== t28 || $[68] !== t30 || $[69] !== t34) {
    t36 = <GiftGivingSection title={t28}>{t30}{t34}{t35}</GiftGivingSection>;
    $[67] = t28;
    $[68] = t30;
    $[69] = t34;
    $[70] = t36;
  } else {
    t36 = $[70];
  }
  let t37;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <Divider className="my-comfy-xl" purpose="neutral" />;
    $[71] = t37;
  } else {
    t37 = $[71];
  }
  let t38;
  if ($[72] !== t) {
    t38 = t(`${centerTranslationKey}.title`);
    $[72] = t;
    $[73] = t38;
  } else {
    t38 = $[73];
  }
  let t39;
  if ($[74] !== t) {
    t39 = t(`${centerTranslationKey}.message`);
    $[74] = t;
    $[75] = t39;
  } else {
    t39 = $[75];
  }
  let t40;
  if ($[76] !== t39) {
    t40 = <GiftGivingDescription text={t39} />;
    $[76] = t39;
    $[77] = t40;
  } else {
    t40 = $[77];
  }
  const t41 = contentSettings?.giftAmountsCenterMax || 15000;
  let t42;
  if ($[78] !== t || $[79] !== t41) {
    t42 = t("core:numbers.currencyCustom", {
      amount: t41
    });
    $[78] = t;
    $[79] = t41;
    $[80] = t42;
  } else {
    t42 = $[80];
  }
  let t43;
  if ($[81] !== t42) {
    t43 = <IntlBlock i18nKey={`${centerTranslationKey}.alertText`} values={{
      giftsMax: 4,
      amountMax: t42
    }} />;
    $[81] = t42;
    $[82] = t43;
  } else {
    t43 = $[82];
  }
  let t44;
  if ($[83] !== isContentSettingsLoading || $[84] !== t43) {
    t44 = <GiftGivingAlert text={t43} isLoading={isContentSettingsLoading} />;
    $[83] = isContentSettingsLoading;
    $[84] = t43;
    $[85] = t44;
  } else {
    t44 = $[85];
  }
  let t45;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <div className="flex flex-col gap-comfy-xs"><ActionCard title="Center Test Card Title 1" subtitle="This is a line of dummy text that represents the subtitle" href="#" /><ActionCard title="Center Test Card Title 2" subtitle="This is a line of dummy text that represents the subtitle" href="#" /></div>;
    $[86] = t45;
  } else {
    t45 = $[86];
  }
  let t46;
  if ($[87] !== t38 || $[88] !== t40 || $[89] !== t44) {
    t46 = <GiftGivingSection title={t38}>{t40}{t44}{t45}</GiftGivingSection>;
    $[87] = t38;
    $[88] = t40;
    $[89] = t44;
    $[90] = t46;
  } else {
    t46 = $[90];
  }
  let t47;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = <Divider className="my-comfy-xl" purpose="neutral" />;
    $[91] = t47;
  } else {
    t47 = $[91];
  }
  let t48;
  if ($[92] !== t14 || $[93] !== t26 || $[94] !== t36 || $[95] !== t46) {
    t48 = <>{t14}{t15}{t26}{t27}{t36}{t37}{t46}{t47}</>;
    $[92] = t14;
    $[93] = t26;
    $[94] = t36;
    $[95] = t46;
    $[96] = t48;
  } else {
    t48 = $[96];
  }
  return t48;
};